import React, { useEffect, useRef, useState } from 'react'
import { Bot, Check, Cookie, ExternalLink, MessageCircle, Send, Settings2, ShieldCheck, X } from 'lucide-react'

const CONSENT_KEY = 'ciat_consent_v1'

const guidance = [
  {
    label: 'What is CIAT?',
    keywords: ['what is', 'ciat', 'institute', 'about'],
    answer: 'CIAT is a proposed institutional platform of Chatake Innoworks Private Limited, connecting industry learning, applied research, publishing, and responsible product practice.',
  },
  {
    label: 'Admissions',
    keywords: ['admission', 'apply', 'enrol', 'enroll', 'course', 'programme'],
    answer: 'No CIAT admissions are open through this Institute 1.0 preview. Current internship operations remain on the established Internship Portal.',
    link: ['Open Internship Portal', 'https://internship.chatakeinnoworks.com/'],
  },
  {
    label: 'Recognition',
    keywords: ['recognition', 'approved', 'accreditation', 'affiliation', 'degree', 'diploma', 'certificate'],
    answer: 'CIAT does not currently claim university status, affiliation, accreditation, degree-awarding authority, or a government-recognised qualification. Recognition pathways remain under evaluation.',
  },
  {
    label: 'Research',
    keywords: ['research', 'gfis', 'apollo', 'evidence'],
    answer: 'CIAT’s research layer connects evidence-led programmes such as GFIS and the gated Apollo workstream. Public claims require methods, provenance, contributor roles, and correction controls.',
    link: ['Explore GFIS', 'https://gfis.energy/'],
  },
  {
    label: 'Publishing',
    keywords: ['publish', 'press', 'journal', 'ijaie', 'paper'],
    answer: 'Knowledge work connects to Innoworks Press and IJAIE so technical work can become durable, reviewable public knowledge.',
    link: ['Visit Innoworks Press', 'https://innoworkspress.org/'],
  },
  {
    label: 'Contact',
    keywords: ['contact', 'email', 'talk', 'help', 'company'],
    answer: 'For institutional enquiries, contact Chatake Innoworks through the company’s official website.',
    link: ['Visit Chatake Innoworks', 'https://chatakeinnoworks.com/'],
  },
]

function consentRecord(decision, optional = false) {
  return {
    version: 1,
    decision,
    savedAt: new Date().toISOString(),
    categories: { essential: true, functional: optional, analytics: false, marketing: false },
  }
}

function readConsent() {
  try {
    const value = JSON.parse(window.localStorage.getItem(CONSENT_KEY))
    return value?.version === 1 ? value : null
  } catch {
    return null
  }
}

export function SiteSystems() {
  const [consent, setConsent] = useState(null)
  const [preferencesOpen, setPreferencesOpen] = useState(false)
  const [functional, setFunctional] = useState(false)
  const [guideOpen, setGuideOpen] = useState(false)
  const [question, setQuestion] = useState('')
  const [messages, setMessages] = useState([
    { role: 'assistant', text: 'Welcome. I can explain CIAT’s mandate, current status, learning, research, publishing, and official pathways.' },
  ])
  const inputRef = useRef(null)

  useEffect(() => {
    const saved = readConsent()
    setConsent(saved)
    setFunctional(Boolean(saved?.categories?.functional))
  }, [])

  useEffect(() => {
    if (guideOpen) inputRef.current?.focus()
  }, [guideOpen])

  const saveConsent = (record) => {
    try { window.localStorage.setItem(CONSENT_KEY, JSON.stringify(record)) } catch { /* Storage can be unavailable. */ }
    setConsent(record)
    setPreferencesOpen(false)
  }

  const ask = (raw) => {
    const text = raw.trim()
    if (!text) return
    const normalized = text.toLowerCase()
    const match = guidance
      .map((item) => ({ item, score: item.keywords.reduce((sum, keyword) => sum + (normalized.includes(keyword) ? keyword.length : 0), 0) }))
      .sort((a, b) => b.score - a.score)[0]
    const reply = match?.score
      ? match.item
      : { answer: 'I could not match that question confidently. Please use an approved topic or contact Chatake Innoworks for an authoritative answer.', link: ['Official company website', 'https://chatakeinnoworks.com/'] }
    setMessages((current) => [...current, { role: 'user', text }, { role: 'assistant', text: reply.answer, link: reply.link }])
    setQuestion('')
  }

  return (
    <div className="ciat-systems" data-testid="ciat-site-systems">
      <button className="ciat-guide-trigger" type="button" onClick={() => setGuideOpen(!guideOpen)} aria-expanded={guideOpen} aria-controls="ciat-guide-panel">
        <MessageCircle size={19} aria-hidden="true" /> <span>CIAT AI Guide</span>
      </button>

      <aside id="ciat-guide-panel" className={`ciat-guide ${guideOpen ? 'is-open' : ''}`} aria-hidden={!guideOpen} aria-label="CIAT AI Guide">
        <header>
          <span className="ciat-guide__mark"><Bot size={20} aria-hidden="true" /></span>
          <span><strong>CIAT AI Guide</strong><small>Private, on-device institutional guidance</small></span>
          <button type="button" onClick={() => setGuideOpen(false)} aria-label="Close CIAT AI Guide"><X size={19} /></button>
        </header>
        <div className="ciat-guide__messages" aria-live="polite">
          {messages.map((message, index) => (
            <div className={`ciat-message ciat-message--${message.role}`} key={`${message.role}-${index}`}>
              <p>{message.text}</p>
              {message.link && <a href={message.link[1]} target="_blank" rel="noreferrer">{message.link[0]} <ExternalLink size={12} /></a>}
            </div>
          ))}
        </div>
        <div className="ciat-guide__shortcuts">
          {guidance.slice(0, 5).map((item) => <button type="button" key={item.label} onClick={() => ask(item.label)}>{item.label}</button>)}
        </div>
        <form onSubmit={(event) => { event.preventDefault(); ask(question) }}>
          <input ref={inputRef} value={question} onChange={(event) => setQuestion(event.target.value)} maxLength={220} placeholder="Ask about CIAT" aria-label="Ask the CIAT AI Guide" />
          <button type="submit" aria-label="Send question"><Send size={18} /></button>
        </form>
        <footer><span>Guidance only · No personal data requested</span><button type="button" onClick={() => setPreferencesOpen(true)}>Privacy choices</button></footer>
      </aside>

      {!consent && (
        <section className="ciat-cookie" aria-label="Cookie and privacy notice">
          <span className="ciat-cookie__icon"><Cookie size={21} aria-hidden="true" /></span>
          <div><strong>Your privacy choices</strong><p>CIAT uses essential browser storage for consent and reliable operation. Optional storage remains off unless you choose it. No advertising or analytics services are currently loaded.</p></div>
          <div className="ciat-cookie__actions">
            <button type="button" onClick={() => saveConsent(consentRecord('essential-only'))}>Essential only</button>
            <button type="button" onClick={() => setPreferencesOpen(true)}><Settings2 size={15} /> Manage</button>
            <button className="primary" type="button" onClick={() => saveConsent(consentRecord('accepted', true))}>Accept</button>
          </div>
        </section>
      )}

      {preferencesOpen && (
        <div className="ciat-consent-backdrop" role="presentation">
          <section className="ciat-consent" role="dialog" aria-modal="true" aria-labelledby="ciat-consent-title">
            <header><span><ShieldCheck size={22} /><strong id="ciat-consent-title">Privacy preferences</strong></span><button type="button" onClick={() => setPreferencesOpen(false)} aria-label="Close privacy preferences"><X size={19} /></button></header>
            <p>Choose the optional storage CIAT may use. Analytics and marketing remain unavailable in this preview.</p>
            <label><span><strong>Essential</strong><small>Consent, security, and basic operation</small></span><input type="checkbox" checked disabled /></label>
            <label><span><strong>Functional</strong><small>Convenience features and saved interface preferences</small></span><input type="checkbox" checked={functional} onChange={(event) => setFunctional(event.target.checked)} /></label>
            <label><span><strong>Analytics</strong><small>No analytics service is currently installed</small></span><input type="checkbox" disabled /></label>
            <label><span><strong>Marketing</strong><small>No advertising or marketing tracker is installed</small></span><input type="checkbox" disabled /></label>
            <div className="ciat-consent__actions"><button type="button" onClick={() => saveConsent(consentRecord('essential-only'))}>Reject optional</button><button className="primary" type="button" onClick={() => saveConsent(consentRecord('custom', functional))}><Check size={15} /> Save choices</button></div>
          </section>
        </div>
      )}
    </div>
  )
}
